import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Stethoscope, AlertTriangle, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { opendentalAPI } from '../services/api';

// ---------------------------------------------------------------------------
// Timezone-safe helpers.
//
// OpenDental slot strings are practice-LOCAL "YYYY-MM-DD HH:mm:ss" with NO
// timezone. We must never round-trip them through `new Date(str)` for the
// purpose of building another slot string, because that reinterprets the
// string in the *browser's* timezone and shifts the wall-clock time.
//
// - To DISPLAY a slot we just split on space and read the HH:mm substring.
// - To BUILD a slot string from a Date (degraded picker) we read the Date's
//   LOCAL components and format manually. No toISOString() anywhere.
// ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');

// "YYYY-MM-DD HH:mm:ss" -> "HH:mm"  (display only, no Date involved)
const slotTimeLabel = (slotStart) => {
  if (!slotStart) return '';
  const parts = String(slotStart).split(' ');
  return (parts[1] || '').slice(0, 5); // HH:mm
};

// Calendar grid only — building day buttons from real Dates is fine because
// we never feed a tz-shifted value back into a slot string. We format the
// day key from LOCAL components.
const dayKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAYS_PER_PAGE = 7;       // one week visible at a time, no horizontal scroll
const MAX_WEEKS = 12;          // ~3-month booking horizon, paged by the ‹ › arrows

// Build one page (a week) of calendar days starting `weekOffset` weeks from
// today. weekOffset 0 = the week beginning today.
const buildWeek = (weekOffset) => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startIdx = weekOffset * DAYS_PER_PAGE;
  for (let i = 0; i < DAYS_PER_PAGE; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + startIdx + i);
    days.push({
      key: dayKey(d),
      weekday: WEEKDAYS[d.getDay()],
      dayNum: d.getDate(),
      monthLabel: MONTHS[d.getMonth()],
    });
  }
  return days;
};

// Degraded picker: build "YYYY-MM-DD HH:mm:00" from a Date's LOCAL
// components. Never toISOString().
const dateToSlotString = (date) => {
  if (!date) return null;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:00`
  );
};

// OpenDental abbreviations encode the role (DOC* = dentist, HYG* = hygienist).
// We show the real name + role instead of the cryptic code.
const providerRole = (abbr) => (/^HYG/i.test(abbr || '') ? 'Hygienist' : 'Dentist');

const Spinner = ({ className = '' }) => (
  <svg className={`animate-spin h-4 w-4 ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const OpenDentalBookingModal = ({ open, lead, targetStage, onBooked, onClose }) => {
  // mode: null while options are loading, 'live' once booking-options load,
  // 'degraded' if OpenDental is unreachable.
  const [mode, setMode] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [options, setOptions] = useState(null);

  // Branch (location) selection — step 1 of the flow. With one configured
  // branch we auto-select and the picker stays hidden; with several the
  // operator chooses before provider/time load.
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  // LIVE selections. We only let the user pick the PROVIDER (the person who
  // sees the patient) + appointment type. The operatory (room) is NOT a user
  // choice: a provider is only schedulable in specific operatories, so we
  // query availability by provider alone and book into the operatory
  // OpenDental returns on the chosen slot. This avoids the confusing case
  // where a hand-picked room doesn't match the selected provider.
  const [providerNum, setProviderNum] = useState('');
  const [appointmentTypeNum, setAppointmentTypeNum] = useState('');
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD"
  const [slots, setSlots] = useState([]); // [{ start, opNum, provNum }]
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // a slot object

  // The selected provider's already-booked appointments for the chosen day,
  // used to flag (non-blocking) when a slot would overlap an existing one.
  const [providerAppts, setProviderAppts] = useState([]); // [{ start, lengthMinutes }]

  // DEGRADED selection
  const [degradedDate, setDegradedDate] = useState(null);

  // Booking flight + error
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState('');
  const [showPendingFallback, setShowPendingFallback] = useState(false);

  // Day picker pages a week at a time; ‹ › move weekOffset.
  const [weekOffset, setWeekOffset] = useState(0);
  const days = useMemo(() => buildWeek(weekOffset), [weekOffset]);
  const weekRangeLabel = useMemo(() => {
    if (!days.length) return '';
    const a = days[0];
    const b = days[days.length - 1];
    return `${a.dayNum} ${a.monthLabel} – ${b.dayNum} ${b.monthLabel}`;
  }, [days]);

  // -- Step 1: load configured branches whenever the modal opens -----------
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    // Reset everything on (re)open so a previous booking's state doesn't leak.
    setMode(null);
    setOptions(null);
    setBranches([]);
    setSelectedLocationId('');
    setProviderNum('');
    setAppointmentTypeNum('');
    setSelectedDay(null);
    setWeekOffset(0);
    setSlots([]);
    setSelectedSlot(null);
    setProviderAppts([]);
    setDegradedDate(null);
    setBookError('');
    setShowPendingFallback(false);
    setBranchesLoading(true);

    opendentalAPI
      .getBookingBranches()
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data?.branches) ? data.branches : [];
        setBranches(list);
        // One branch → auto-select and skip the picker. Several → wait for
        // the operator. None → degraded (let them save a pending date).
        if (list.length === 1) setSelectedLocationId(list[0].id);
        else if (list.length === 0) setMode('degraded');
      })
      .catch(() => {
        if (cancelled) return;
        setMode('degraded');
      })
      .finally(() => {
        if (!cancelled) setBranchesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  // -- Step 2: load booking options once a branch is chosen ----------------
  useEffect(() => {
    if (!open || !selectedLocationId) return;
    let cancelled = false;

    // Reset per-branch selections (a branch switch invalidates everything).
    setMode(null);
    setOptions(null);
    setProviderNum('');
    setAppointmentTypeNum('');
    setSelectedDay(null);
    setWeekOffset(0);
    setSlots([]);
    setSelectedSlot(null);
    setProviderAppts([]);
    setBookError('');
    setShowPendingFallback(false);
    setOptionsLoading(true);

    opendentalAPI
      .getBookingOptions(selectedLocationId)
      .then(({ data }) => {
        if (cancelled) return;
        setOptions(data);
        // Pre-select provider + type from defaults (fall back to first item).
        const d = data?.defaults || {};
        const firstProvider = data?.providers?.[0]?.providerNum;
        const firstType = data?.appointmentTypes?.[0]?.appointmentTypeNum;
        setProviderNum(String(d.providerNum ?? firstProvider ?? ''));
        setAppointmentTypeNum(String(d.appointmentTypeNum ?? firstType ?? ''));
        setMode('live');
      })
      .catch(() => {
        if (cancelled) return;
        // 502 (OD down) or anything else -> degraded mode.
        setMode('degraded');
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedLocationId]);

  const selectedType = useMemo(
    () =>
      options?.appointmentTypes?.find(
        (t) => String(t.appointmentTypeNum) === String(appointmentTypeNum)
      ) || null,
    [options, appointmentTypeNum]
  );

  // -- Fetch slots when provider / type / day are chosen --------------------
  useEffect(() => {
    if (mode !== 'live') return;
    if (!providerNum || !appointmentTypeNum || !selectedDay) {
      setSlots([]);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    setSelectedSlot(null);
    setBookError('');
    setShowPendingFallback(false);

    opendentalAPI
      .getSlots(selectedLocationId, {
        providerNum,
        dateStart: selectedDay,
        dateEnd: selectedDay,
        lengthMinutes: selectedType?.durationMinutes,
      })
      .then(({ data }) => {
        if (cancelled) return;
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });

    // In parallel, pull the provider's already-booked appointments for the day
    // so we can flag (without blocking) a slot that would double-book them.
    // Best-effort: a failure here just means no warning, never a broken modal.
    opendentalAPI
      .getProviderAppointments(selectedLocationId, {
        providerNum,
        dateStart: selectedDay,
        dateEnd: selectedDay,
      })
      .then(({ data }) => {
        if (cancelled) return;
        setProviderAppts(Array.isArray(data?.appointments) ? data.appointments : []);
      })
      .catch(() => {
        if (!cancelled) setProviderAppts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, selectedLocationId, providerNum, appointmentTypeNum, selectedDay, selectedType]);

  // Esc to close (only when not mid-booking).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape' && !booking) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, booking, onClose]);

  const providerLabel = (num) => {
    const p = options?.providers?.find((x) => String(x.providerNum) === String(num));
    return p ? (p.name || p.abbr || `#${num}`) : `#${num}`;
  };
  const operatoryLabel = (num) => {
    const o = options?.operatories?.find((x) => String(x.operatoryNum) === String(num));
    return o ? o.name : (num != null ? `Op ${num}` : '');
  };

  const selectedDayLabel = useMemo(() => {
    if (!selectedDay) return '';
    const d = days.find((x) => x.key === selectedDay);
    return d ? `${d.dayNum} ${d.monthLabel}` : selectedDay;
  }, [selectedDay, days]);

  // The day's time grid = OpenDental's open slots PLUS the provider's already
  // booked times (which OD omits from the open-slot feed). Booked times are
  // surfaced as selectable "busy" cells in a distinct colour so the operator
  // can SEE them and, if they really want, double-book on purpose. Sorted by
  // clock time; a booked time already present as an open slot isn't duplicated.
  const timeCells = useMemo(() => {
    const open = (slots || []).map((s) => ({
      key: `open-${s.start}-${s.opNum}`,
      start: s.start,
      busy: false,
      slot: s,
    }));
    const openStarts = new Set(open.map((o) => String(o.start).slice(0, 16)));
    // Expand each booked appointment across its FULL span — not just its start
    // — so a long appointment shows every grid step it occupies (e.g. a
    // 10:00–16:00 block fills 10:00, 10:30 … 15:30), instead of leaving a gap
    // in the middle of the day. Stepped by the chosen appointment length so it
    // lines up with the open-slot grid. Deduped by time; a step already
    // offered as an open slot stays open.
    const step = Math.max(5, Math.round(selectedType?.durationMinutes || 30));
    const pad = (n) => String(n).padStart(2, '0');
    const toMin = (s) => {
      const [h, m] = String(s).slice(11, 16).split(':');
      return (Number(h) || 0) * 60 + (Number(m) || 0);
    };
    const seenBusy = new Set();
    const busy = [];
    for (const a of providerAppts || []) {
      if (!a || !a.start) continue;
      const datePart = String(a.start).slice(0, 10);
      const startMin = toMin(a.start);
      const endMin = startMin + (a.lengthMinutes || step);
      for (let m = startMin; m < endMin; m += step) {
        const cellStart = `${datePart} ${pad(Math.floor(m / 60))}:${pad(m % 60)}:00`;
        const k = cellStart.slice(0, 16);
        if (openStarts.has(k) || seenBusy.has(k)) continue;
        seenBusy.add(k);
        busy.push({
          key: `busy-${k}`,
          start: cellStart,
          busy: true,
          // Book into the same provider; reuse the existing appointment's
          // operatory (the backend resolves a room if it's missing).
          slot: { start: cellStart, opNum: a.operatoryNum ?? null, provNum: Number(providerNum) || a.providerNum },
        });
      }
    }
    return [...open, ...busy].sort((x, y) =>
      x.start < y.start ? -1 : x.start > y.start ? 1 : 0
    );
  }, [slots, providerAppts, providerNum, selectedType]);

  // Existing provider appointments that overlap the selected slot — a
  // double-booking. Wall-clock minutes-of-day comparison (same day, no TZ).
  const slotConflicts = useMemo(() => {
    if (mode !== 'live' || !selectedSlot) return [];
    const toMin = (s) => {
      const [h, m] = String(s).slice(11, 16).split(':');
      return (Number(h) || 0) * 60 + (Number(m) || 0);
    };
    const start = toMin(selectedSlot.start);
    const end = start + (selectedType?.durationMinutes || 0);
    return (providerAppts || []).filter((a) => {
      const aStart = toMin(a.start);
      const aEnd = aStart + (a.lengthMinutes || 0);
      return start < aEnd && aStart < end; // overlap
    });
  }, [mode, selectedSlot, providerAppts, selectedType]);

  // -- Booking actions -----------------------------------------------------
  const doBook = async (bookMode) => {
    if (booking) return;
    setBooking(true);
    setBookError('');
    try {
      const body =
        bookMode === 'live' || mode === 'live'
          ? {
              leadId: lead?._id,
              locationId: selectedLocationId || null,
              mode: bookMode,
              providerNum: selectedSlot?.provNum ?? providerNum,
              // The slot's own operatory is authoritative — book into the
              // operatory OpenDental scheduled the opening under.
              operatoryNum: selectedSlot?.opNum ?? null,
              appointmentTypeNum,
              slotStart: selectedSlot?.start,
              pattern: selectedType?.pattern,
              // Display labels for the CRM timeline note.
              providerName: providerLabel(selectedSlot?.provNum ?? providerNum),
              appointmentTypeName: selectedType?.name,
            }
          : {
              leadId: lead?._id,
              locationId: selectedLocationId || null,
              mode: 'pending',
              providerNum: null,
              operatoryNum: null,
              appointmentTypeNum: null,
              slotStart: dateToSlotString(degradedDate),
            };
      const { data } = await opendentalAPI.bookAppointment(body);
      onBooked(data);
    } catch (err) {
      // 502 from a live booking = OD failed at the moment of booking. Offer
      // the "save anyway / sync later" fallback. For the degraded path a
      // failure here is unexpected; surface the message inline.
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Something went wrong while creating the appointment.';
      setBookError(msg);
      if (mode === 'live' && bookMode === 'live') {
        setShowPendingFallback(true);
      }
    } finally {
      setBooking(false);
    }
  };

  const confirmDisabled =
    booking ||
    mode === null ||                                   // branch not chosen / still loading
    (mode === 'live' && !selectedSlot) ||
    (mode === 'degraded' && !degradedDate);

  const selectClass =
    'w-full px-2 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500';

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={booking ? undefined : onClose}
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden relative flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Schedule Appointment</h3>
                    {lead && (
                      <p className="text-xs text-gray-500">
                        {lead.firstName} {lead.lastName}
                        {lead.company ? ` — ${lead.company}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                {!booking && (
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="p-5 overflow-y-auto flex-1">
                {/* Branch picker — only when the org has more than one
                    configured branch. With one it's auto-selected upstream. */}
                {branches.length > 1 && (
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                      Branch
                    </label>
                    <select
                      value={selectedLocationId}
                      onChange={(e) => setSelectedLocationId(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Select a branch…</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Loading branches */}
                {branchesLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                    <Spinner className="text-teal-600" /> Loading branches…
                  </div>
                )}

                {/* Multi-branch, none chosen yet — prompt before anything loads */}
                {!branchesLoading && branches.length > 1 && !selectedLocationId && (
                  <p className="text-sm text-gray-400 py-4 text-center">
                    Pick a branch to see availability.
                  </p>
                )}

                {/* Loading options */}
                {optionsLoading && (
                  <div className="space-y-4 animate-pulse">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="h-10 bg-gray-100 rounded-md" />
                      <div className="h-10 bg-gray-100 rounded-md" />
                      <div className="h-10 bg-gray-100 rounded-md" />
                    </div>
                    <div className="flex gap-2">
                      {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="h-14 w-14 bg-gray-100 rounded-lg" />
                      ))}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-9 bg-gray-100 rounded-md" />
                      ))}
                    </div>
                  </div>
                )}

                {/* LIVE MODE */}
                {!optionsLoading && mode === 'live' && options && (
                  <div className="space-y-5">
                    {/* Selects — provider (who sees the patient) + appointment
                        type. The room/operatory is assigned automatically from
                        the chosen slot, so it isn't a manual choice. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          Provider
                        </label>
                        <select
                          value={providerNum}
                          onChange={(e) => setProviderNum(e.target.value)}
                          className={selectClass}
                        >
                          {(options.providers || []).map((p) => (
                            <option key={p.providerNum} value={p.providerNum}>
                              {p.name} ({providerRole(p.abbr)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          Appointment Type
                        </label>
                        <select
                          value={appointmentTypeNum}
                          onChange={(e) => setAppointmentTypeNum(e.target.value)}
                          className={selectClass}
                        >
                          {(options.appointmentTypes || []).map((t) => (
                            <option key={t.appointmentTypeNum} value={t.appointmentTypeNum}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Day picker — one week per page, ‹ › to move weeks */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Day
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
                            disabled={weekOffset === 0}
                            className="p-1 rounded-md border border-gray-200 text-gray-600 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Previous week"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <span className="text-xs text-gray-500 w-32 text-center tabular-nums">
                            {weekRangeLabel}
                          </span>
                          <button
                            type="button"
                            onClick={() => setWeekOffset((w) => Math.min(MAX_WEEKS - 1, w + 1))}
                            disabled={weekOffset >= MAX_WEEKS - 1}
                            className="p-1 rounded-md border border-gray-200 text-gray-600 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Next week"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-2">
                        {days.map((d) => {
                          const active = selectedDay === d.key;
                          return (
                            <button
                              key={d.key}
                              type="button"
                              onClick={() => setSelectedDay(d.key)}
                              className={`h-16 rounded-lg border flex flex-col items-center justify-center transition-colors ${
                                active
                                  ? 'bg-teal-600 border-teal-600 text-white'
                                  : 'bg-white border-gray-200 text-gray-700 hover:border-teal-400'
                              }`}
                            >
                              <span className="text-[10px] uppercase tracking-wide">{d.weekday}</span>
                              <span className="text-lg font-semibold leading-none mt-0.5">{d.dayNum}</span>
                              <span className="text-[10px]">{d.monthLabel}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Slots */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Available Times
                      </label>
                      {!selectedDay ? (
                        <p className="text-sm text-gray-400">Pick a day to see available times.</p>
                      ) : slotsLoading ? (
                        <div className="grid grid-cols-4 gap-2 animate-pulse">
                          {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-9 bg-gray-100 rounded-md" />
                          ))}
                        </div>
                      ) : timeCells.length === 0 ? (
                        <p className="text-sm text-gray-500">No available times for this day.</p>
                      ) : (
                        <>
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {timeCells.map((cell) => {
                              const active = selectedSlot?.start === cell.start;
                              const cls = active
                                ? cell.busy
                                  ? 'bg-amber-500 border-amber-500 text-white'
                                  : 'bg-teal-600 border-teal-600 text-white'
                                : cell.busy
                                ? 'bg-amber-50 border-amber-300 text-amber-700 hover:border-amber-400'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-teal-400';
                              return (
                                <button
                                  key={cell.key}
                                  type="button"
                                  title={cell.busy ? 'Provider already has an appointment at this time' : undefined}
                                  onClick={() => setSelectedSlot(cell.slot)}
                                  className={`px-2 py-1.5 text-sm rounded-md border transition-colors ${cls}`}
                                >
                                  {slotTimeLabel(cell.start)}
                                </button>
                              );
                            })}
                          </div>
                          {timeCells.some((c) => c.busy) && (
                            <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
                              <span className="inline-block w-3 h-3 rounded-sm bg-amber-50 border border-amber-300" />
                              Amber times are already booked for this provider — still selectable, but they create a double booking.
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Selected provider + room (operatory). The room is the
                        OpenDental operatory the chosen slot maps to, e.g.
                        "819 Cosmecsc chair" — shown so the office can confirm
                        which chair/room the appointment lands in. */}
                    {selectedSlot && (
                      <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm space-y-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-500">Provider</span>
                          <span className="font-medium text-gray-900 text-right truncate">
                            {providerLabel(selectedSlot.provNum ?? providerNum)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-500">Room</span>
                          <span className="font-medium text-gray-900 text-right truncate">
                            {operatoryLabel(selectedSlot.opNum)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Double-booking warning. Non-blocking: the provider can
                        still be booked at this time, we just make the existing
                        appointment visible so the office confirms it on purpose. */}
                    {slotConflicts.length > 0 && (
                      <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm">
                        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>{providerLabel(selectedSlot.provNum ?? providerNum)}</strong> already has{' '}
                          {slotConflicts.length} overlapping appointment{slotConflicts.length > 1 ? 's' : ''} at
                          this time ({[...new Set(slotConflicts.map((c) => slotTimeLabel(c.start)))].join(', ')}).
                          Booking this slot stacks another on top — the provider will have multiple appointments
                          at once.
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* DEGRADED MODE */}
                {!optionsLoading && mode === 'degraded' && (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm">
                      <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>OpenDental is unreachable, so availability can&apos;t be checked right now.</strong>{' '}
                        Pick a date &amp; time — we&apos;ll save it and sync automatically when OpenDental is
                        back. The appointment will be created with the practice&apos;s default provider, and
                        the office may adjust the time if needed.
                      </span>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                        Appointment Date &amp; Time
                      </label>
                      <div className="relative">
                        <DatePicker
                          selected={degradedDate}
                          onChange={(d) => setDegradedDate(d)}
                          showTimeSelect
                          timeIntervals={15}
                          dateFormat="d MMM yyyy HH:mm"
                          placeholderText="Select date and time"
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          wrapperClassName="w-full"
                        />
                        <Calendar
                          size={16}
                          className="absolute right-3 top-2.5 text-gray-400 pointer-events-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Booking error + pending fallback */}
                {bookError && (
                  <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-300 text-red-800 text-sm">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                      <span>{bookError}</span>
                    </div>
                    {showPendingFallback && (
                      <button
                        type="button"
                        onClick={() => doBook('pending')}
                        disabled={booking}
                        className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 disabled:opacity-50"
                      >
                        {booking && <Spinner className="text-amber-900" />}
                        Save anyway, sync to OpenDental later
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
                <div className="text-xs text-gray-600 truncate">
                  {mode === 'live' && selectedSlot && (
                    <span>
                      {providerLabel(selectedSlot.provNum ?? providerNum)} ·{' '}
                      {operatoryLabel(selectedSlot.opNum)} · {selectedType?.name} ·{' '}
                      {selectedDayLabel} {slotTimeLabel(selectedSlot.start)}
                    </span>
                  )}
                  {mode === 'degraded' && degradedDate && (
                    <span>Pending sync · {dateToSlotString(degradedDate)?.slice(0, 16)}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={onClose}
                    disabled={booking}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => doBook(mode === 'degraded' ? 'pending' : 'live')}
                    disabled={confirmDisabled}
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {booking && <Spinner className="text-white" />}
                    {mode === 'degraded' ? 'Save Date' : 'Confirm Appointment'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OpenDentalBookingModal;
